import { MdGridView, MdListAlt, MdDehaze } from 'react-icons/md';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MiniStatistics from 'components/card/MiniStatistics';
import { dateToFrench } from 'utils/date';

import Card from 'components/card';
import { getPreparations } from 'interfaces/orderPreparation';
import { IOrderPreparation, IPreparationLine } from 'interfaces/orderPreparationTypes';
import PreparationCard from 'components/card/preparationCard';
import SearchIcon from 'components/icons/SearchIcon';
import { FaArrowRotateRight } from 'react-icons/fa6';
import { getUsersLight } from 'interfaces/user';

const statusLabels: Record<string, string> = {
    draft: 'Brouillon',
    in_progress: 'En cours',
    completed: 'Terminée',
    cancelled: 'Annulée'
};

const statusColors: Record<string, string> = {
    draft: 'bg-gray-500',
    in_progress: 'bg-[#0260CB]',
    completed: 'bg-green-500',
    cancelled: 'bg-red-500'
};

const ListPreparations = () => {
    const nav = useNavigate();
    const [preparations, setPreparations] = useState<IOrderPreparation[] | null>(null);
    const [filteredPreparations, setFilteredPreparations] = useState<IOrderPreparation[] | null>(null);
    const [search, setSearch] = useState('');
    const [view, setView] = useState<string>(() => localStorage.getItem('preparations-view') || 'grid');

    const handleSetView = (newView: string) => {
        setView(newView);
        localStorage.setItem('preparations-view', newView);
    };
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [dateFilter, setDateFilter] = useState<string>('all'); // 'all', 'today', 'week', 'month'
    const [preparerFilter, setPreparerFilter] = useState<string>('all');
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const statusLabels = {
        all: 'Toutes',
        draft: 'Brouillon',
        in_progress: 'En cours',
        completed: 'Terminées',
        cancelled: 'Annulées'
    };

    const dateLabels = {
        all: 'Toutes dates',
        today: "Aujourd'hui",
        week: 'Cette semaine',
        month: 'Ce mois'
    };

    const fetchPreparations = async () => {
        setLoading(true);
        const filters = statusFilter !== 'all' ? { status: statusFilter } : undefined;
        const res = await getPreparations(filters);
        if (res) {
            console.log('[DEBUG] première prépa:', JSON.stringify(res[0], null, 2));
            setPreparations(res);
            setFilteredPreparations(res);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchPreparations();
        
        // Fetch users for preparer filter
        (async () => {
            const u = await getUsersLight();
            if (u) setUsers(u);
        })();
    }, [statusFilter]);

    useEffect(() => {
        if (!preparations) return;
        
        let filtered = [...preparations];
        
        // Filter by search
        if (search.trim() !== '') {
            filtered = filtered.filter((prep) => 
                prep.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
                prep.preparationLines.some((line: IPreparationLine) => 
                    line.productName.toLowerCase().includes(search.toLowerCase())
                )
            );
        }
        
        // Filter by date
        if (dateFilter !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            
            filtered = filtered.filter((prep) => {
                const prepDate = new Date(prep.createdAt);
                
                if (dateFilter === 'today') {
                    return prepDate >= today;
                } else if (dateFilter === 'week') {
                    const weekAgo = new Date(today);
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return prepDate >= weekAgo;
                } else if (dateFilter === 'month') {
                    const monthAgo = new Date(today);
                    monthAgo.setMonth(monthAgo.getMonth() - 1);
                    return prepDate >= monthAgo;
                }
                return true;
            });
        }
        
        // Filter by preparer
        if (preparerFilter !== 'all') {
            filtered = filtered.filter((prep) => {
                // Check if preparedBy exists and matches
                if (!prep.preparedBy) return false;
                
                // Handle both object format {_id, fullName} and string format
                const preparerId = typeof prep.preparedBy === 'object' && prep.preparedBy._id 
                    ? prep.preparedBy._id 
                    : prep.preparedBy;
                
                return preparerId === preparerFilter;
            });
        }
        
        setFilteredPreparations(filtered);
    }, [search, preparations, dateFilter, preparerFilter]);

    const stats = {
        total: preparations?.length || 0,
        draft: preparations?.filter(p => p.status === 'draft').length || 0,
        in_progress: preparations?.filter(p => p.status === 'in_progress').length || 0,
        completed: preparations?.filter(p => p.status === 'completed').length || 0,
    };

    const getPreparerName = (preparedBy: any): string | null => {
        if (!preparedBy) return null;
        if (typeof preparedBy === 'object' && preparedBy.fullName) return preparedBy.fullName;
        if (typeof preparedBy === 'string') {
            // If it looks like a MongoDB ObjectId (24 hex chars), it's an unresolved ID
            if (/^[a-f\d]{24}$/i.test(preparedBy)) return null;
            return preparedBy;
        }
        return null;
    };

    return (
        <div className="mt-4 grid h-full w-full grid-cols-1 gap-5 xl:mt-3">
            {/* Stats Cards */}
            <div className="z-0 col-span-12 grid h-full w-full grid-cols-2 gap-3 sm:gap-5 md:grid-cols-2 xl:grid-cols-4">
                <MiniStatistics
                    name="Total"
                    value={stats.total.toString()}
                    icon={<MdListAlt color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Brouillon"
                    value={stats.draft.toString()}
                    icon={<MdListAlt color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="En cours"
                    value={stats.in_progress.toString()}
                    icon={<MdListAlt color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Terminées"
                    value={stats.completed.toString()}
                    icon={<MdListAlt color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
            </div>

            {/* Main Card */}
            <div className="col-span-12 h-full w-full rounded-[20px]">
                <Card extra="pt-[10px] pl-[20px] pr-[20px] pb-[20px] border">
                    {/* Status Tabs */}
                    <div className="w-full flex items-center m-auto">
                        <div className="mt-2 flex w-full gap-[2px] overflow-hidden border-b border-gray-200 justify-center flex-row">
                            {Object.entries(statusLabels).map(([key, label]) => (
                                <div
                                    key={key}
                                    className={
                                        statusFilter === key
                                            ? 'border-b-[4px] border-[#0260CB] pb-[8px] pt-[8px] hover:cursor-default dark:border-brand-400 bg-[#7090B0]/[0.05] flex items-center justify-center gap-2 rounded-t-[15px] w-1/5 h-[40px] text-center'
                                            : 'pb-[11px] pt-[8px] hover:cursor-pointer bg-[#7090B0]/[0.15] flex items-center justify-center gap-2 rounded-t-[15px] w-1/5 h-[40px] text-center'
                                    }
                                    onClick={() => setStatusFilter(key)}
                                >
                                    <p className="text-xs font-medium text-navy-700 dark:text-white md:text-[15px]">
                                        {label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Search */}
                    <div className="mt-[20px] flex flex-col sm:flex-row justify-between items-center gap-y-[10px]">
                        <div className="w-full flex gap-5">
                            <div className="flex h-[38px] w-full items-center rounded-xl bg-lightPrimary text-sm text-gray-600 sm:w-[40%]">
                                <SearchIcon />
                                <input
                                    type="text"
                                    placeholder="Rechercher par commande ou produit..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                                />
                            </div>
                            <div className="mt-1">
                                <select
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    className="h-[38px] rounded-xl bg-lightPrimary px-3 text-sm text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                                >
                                    {Object.entries(dateLabels).map(([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mt-1">
                                <select
                                    value={preparerFilter}
                                    onChange={(e) => setPreparerFilter(e.target.value)}
                                    className="h-[38px] rounded-xl bg-lightPrimary px-3 text-sm text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                                >
                                    <option value="all">Tous les préparateurs</option>
                                    {users.map((user: any) => (
                                        <option key={user._id} value={user._id}>
                                            {user.fullName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-2 items-center">
                            <button
                                onClick={fetchPreparations}
                                className="flex items-center justify-center rounded-xl bg-lightPrimary p-2 text-[#0260CB] transition duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10"
                            >
                                <FaArrowRotateRight className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* View Toggle (desktop) */}
                    <div className="hidden lg:block absolute left-6 top-6">
                        <div className="flex gap-2">
                            <MdGridView
                                size={20}
                                className={`${
                                    view === 'grid' && 'text-[#0260CB]'
                                } cursor-pointer`}
                                onClick={() => handleSetView('grid')}
                            />
                            <MdDehaze
                                size={20}
                                className={`${
                                    view === 'list' && 'text-[#0260CB]'
                                } cursor-pointer`}
                                onClick={() => handleSetView('list')}
                            />
                        </div>
                    </div>

                    {/* Content */}
                    {loading ? (
                        <div className="flex h-[400px] items-center justify-center">
                            <div className="text-center">
                                <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#0260CB] border-r-transparent"></div>
                                <p className="text-gray-600">Chargement des préparations...</p>
                            </div>
                        </div>
                    ) : filteredPreparations && filteredPreparations.length > 0 ? (
                        view === 'grid' ? (
                            /* Vue grille – design actuel */
                            <div className="mt-2 grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-4">
                                {filteredPreparations.map((preparation) => (
                                    <PreparationCard
                                        key={preparation._id}
                                        preparation={preparation}
                                    />
                                ))}
                            </div>
                        ) : (
                            /* Vue liste – design style editOrder */
                            <div className="mt-2 overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr>
                                            <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start text-sm dark:border-white/10">
                                                <p className="text-sm font-bold text-gray-600 dark:text-white">Commande</p>
                                            </th>
                                            <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start text-sm dark:border-white/10">
                                                <p className="text-sm font-bold text-gray-600 dark:text-white">Client</p>
                                            </th>
                                            <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start text-sm dark:border-white/10">
                                                <p className="text-sm font-bold text-gray-600 dark:text-white">Statut</p>
                                            </th>
                                            <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start text-sm dark:border-white/10">
                                                <p className="text-sm font-bold text-gray-600 dark:text-white">Préparateur</p>
                                            </th>
                                            <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start text-sm dark:border-white/10">
                                                <p className="text-sm font-bold text-gray-600 dark:text-white">Produits</p>
                                            </th>
                                            <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start text-sm dark:border-white/10">
                                                <p className="text-sm font-bold text-gray-600 dark:text-white">Créée le</p>
                                            </th>
                                            <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start text-sm dark:border-white/10">
                                                <p className="text-sm font-bold text-gray-600 dark:text-white">Validée le</p>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredPreparations.map((preparation) => (
                                            <tr
                                                key={preparation._id}
                                                className="hover:bg-gray-50 dark:hover:bg-navy-700 cursor-pointer"
                                                onClick={() => nav(`/preparations/${preparation._id}`)}
                                            >
                                                <td className="border-b text-sm dark:border-white/10 px-[10px] py-[10px]">
                                                    <p className="text-sm font-medium text-navy-700 dark:text-white">
                                                        {preparation.orderNumber}
                                                    </p>
                                                </td>
                                                <td className="border-b text-sm dark:border-white/10 px-[10px] py-[10px]">
                                                    <p className="text-sm text-navy-700 dark:text-white">
                                                        {preparation.customerName || '—'}
                                                    </p>
                                                </td>
                                                <td className="border-b text-sm dark:border-white/10 px-[10px] py-[10px]">
                                                    <span className={`text-white ${statusColors[preparation.status]} px-2 py-1 rounded-xl text-[10px] font-semibold`}>
                                                        {statusLabels[preparation.status]}
                                                    </span>
                                                </td>
                                                <td className="border-b text-sm dark:border-white/10 px-[10px] py-[10px]">
                                                    {preparation.status === 'in_progress' && getPreparerName(preparation.preparedBy) ? (
                                                        <div className="flex items-center gap-1">
                                                            <span className="inline-block h-2 w-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0"></span>
                                                            <p className="text-sm text-blue-600 font-medium">
                                                                {getPreparerName(preparation.preparedBy)}
                                                            </p>
                                                        </div>
                                                    ) : getPreparerName(preparation.preparedBy) ? (
                                                        <p className="text-sm text-navy-700 dark:text-white">
                                                            {getPreparerName(preparation.preparedBy)}
                                                        </p>
                                                    ) : (
                                                        <p className="text-sm text-gray-400">—</p>
                                                    )}
                                                </td>
                                                <td className="border-b text-sm dark:border-white/10 px-[10px] py-[10px]">
                                                    <p className="text-sm text-navy-700 dark:text-white">
                                                        {preparation.preparationLines.length} produit{preparation.preparationLines.length > 1 ? 's' : ''}
                                                    </p>
                                                    <p className="text-xs text-gray-400">
                                                        {preparation.preparationLines.reduce((s, l) => s + l.quantityPrepared, 0)}
                                                        /{preparation.preparationLines.reduce((s, l) => s + l.quantityOrdered, 0)} préparés
                                                    </p>
                                                </td>
                                                <td className="border-b text-sm dark:border-white/10 px-[10px] py-[10px]">
                                                    <p className="text-sm text-navy-700 dark:text-white">
                                                        {dateToFrench(preparation.createdAt)}
                                                    </p>
                                                </td>
                                                <td className="border-b text-sm dark:border-white/10 px-[10px] py-[10px]">
                                                    <p className="text-sm text-navy-700 dark:text-white">
                                                        {preparation.validatedAt ? dateToFrench(preparation.validatedAt) : '—'}
                                                    </p>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    ) : (
                        <div className="flex h-[400px] items-center justify-center">
                            <div className="text-center">
                                <p className="text-xl font-semibold text-gray-600">
                                    Aucune préparation trouvée
                                </p>
                                <p className="mt-2 text-sm text-gray-400">
                                    {search ? 'Essayez avec d\'autres mots-clés' : 'Créez une préparation depuis une commande'}
                                </p>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default ListPreparations;
